import PropTypes from "prop-types";
import React, { useEffect, useMemo, useState } from "react";
import styled, { createGlobalStyle } from "styled-components";
import Breadcrumbs from "../based/Breadcrumbs";
import CommonTable from "../based/CommonTable";
import { INITPAGING, NOTIFY } from "../based/Constants";
import { ConfirmDialog } from "../based/Dialog";
import Loading from "../based/Loading";
import CommonModal from "../based/Modal";
import { Notify } from "../based/Notify";
import Request from "../based/Request";
import EmbroideryServices from "../based/services/EmbroideryServices";
import EmbroideryDstCanvas3D from "./EmbroideryDstCanvas3D";

const DEFAULT_FONT_SIZES_MM = [10, 15, 20, 25, 30];
const DEFAULT_LETTER_SPACING_RATIO = 0.1;
const DEFAULT_LINE_SPACING_RATIO = 0.3;
const DEFAULT_BASELINE_RATIO = 0.8;
const FONT_CONFIG_PREVIEW_PX_PER_MM = 6;
const FONT_CONFIG_PREVIEW_MIN_WIDTH = 420;
const FONT_CONFIG_PREVIEW_MIN_HEIGHT = 150;
const FONT_CONFIG_PREVIEW_MODAL_PX_PER_MM = 10;
const FONT_CONFIG_PREVIEW_MODAL_MIN_WIDTH = 920;
const FONT_CONFIG_PREVIEW_MODAL_MIN_HEIGHT = 320;
const DEFAULT_GLYPH_GROUPS = {
    Upper: "ABCDEFGHIJKLMNOPQRSTUVWXYZ",
    Lower: "abcdefghijklmnopqrstuvwxyz",
    Number: "0123456789",
};
const DEFAULT_PAGING = { ...INITPAGING, pageSize: 20 };

// Tạo dòng glyph rỗng, dùng chung khi admin thêm tay hoặc parse từ tên file upload.
function createEmptyGlyph(character) {
    const value = character || "";
    return {
        id: 0,
        character: value,
        unicodeCodePoint: value ? value.codePointAt(0) : 0,
        dstFileUrl: "",
        dstFileName: "",
        pngFileUrl: "",
        pngFileName: "",
        svgFileUrl: "",
        svgFileName: "",
        widthMm: 0,
        heightMm: 0,
        baselineMm: 0,
        stitchCount: 0,
        fileSizeByte: 0,
        isActive: true,
        sortOrder: 0,
        underlayText: "",
        underlays: [],
    };
}

// Tạo size mặc định cho font thêu, mỗi size chứa danh sách glyph riêng.
function getDefaultFontSizeMetrics(sizeMm) {
    const numericSize = Number(sizeMm) || 0;
    return {
        defaultLetterSpacingMm: numericSize ? Number((numericSize * DEFAULT_LETTER_SPACING_RATIO).toFixed(2)) : 0,
        defaultLineSpacingMm: numericSize ? Number((numericSize * DEFAULT_LINE_SPACING_RATIO).toFixed(2)) : 0,
        defaultBaselineMm: numericSize ? Number((numericSize * DEFAULT_BASELINE_RATIO).toFixed(2)) : 0,
    };
}

function createFontSize(sizeMm) {
    const defaultMetrics = getDefaultFontSizeMetrics(sizeMm);
    return {
        id: 0,
        fontId: 0,
        sizeLabel: sizeMm ? `${sizeMm}mm` : "",
        sizeMm: sizeMm || "",
        previewImageUrl: "",
        ...defaultMetrics,
        isDefault: sizeMm == DEFAULT_FONT_SIZES_MM[1],
        isActive: true,
        sortOrder: 0,
        glyphs: [],
    };
}

// Tạo model rỗng cho modal thêm mới font thêu.
function createEmptyFont() {
    return {
        id: 0,
        name: "",
        text: "",
        code: "",
        previewImageUrl: "",
        trueTypeFontFileUrl: "",
        fontFamilyName: "",
        description: "",
        isActive: true,
        sortOrder: 0,
        sizes: DEFAULT_FONT_SIZES_MM.map((size) => createFontSize(size)),
    };
}

// Chuẩn hóa số từ input, giữ chuỗi rỗng để admin dễ thấy field chưa nhập.
function normalizeNumber(value) {
    if (value === "" || value === null || value === undefined) return "";
    const parsed = Number(value);
    return Number.isNaN(parsed) ? "" : parsed;
}

// Baseline mặc định đặt ở đáy glyph để giữ hành vi cũ; chữ có chân sẽ được admin chỉnh nhỏ hơn chiều cao.
function getDefaultGlyphBaselineMm(heightMm, currentBaselineMm) {
    const baseline = Number(currentBaselineMm) || 0;
    if (baseline > 0) return baseline;
    return Number(heightMm) || 0;
}

// Lấy ký tự từ tên file DST khi admin upload nhiều file một lượt.
// Parse danh sách underlay từ chuỗi dạng "1_48,113_192" để lưu range stitch theo glyph.
function parseGlyphUnderlaysText(value) {
    return (value || "")
        .split(/[,\s;]+/)
        .map((part) => part.trim())
        .filter(Boolean)
        .map((part, index) => {
            const match = part.match(/^(\d+)[_-](\d+)$/);
            if (!match) return null;
            const startStitchIndex = Number(match[1]);
            const endStitchIndex = Number(match[2]);
            if (!startStitchIndex || !endStitchIndex || endStitchIndex < startStitchIndex) return null;
            return {
                id: 0,
                underlayIndex: index,
                startStitchIndex,
                endStitchIndex,
                isActive: true,
                sortOrder: index,
            };
        })
        .filter(Boolean);
}

// Format underlay về dạng ngắn để admin kiểm tra nhanh sau khi parse tên file.
function formatGlyphUnderlays(underlays) {
    return (underlays || []).map((item) => `${item.startStitchIndex}_${item.endStitchIndex}`).join(", ");
}

// Parse baseline từ hậu tố "#13.00" trong tên file để tự fill khoảng cách từ mép trên tới đường chân chữ.
function parseGlyphBaselineMmText(value) {
    const match = (value || "").trim().match(/^(\d+(?:\.\d+)?)/);
    if (!match) return 0;
    const baselineMm = Number(match[1]);
    return Number.isNaN(baselineMm) ? 0 : baselineMm;
}

// Parse thông tin glyph từ tên file; hỗ trợ format mới "â_1_27,78_121 #13.00.dst" và format cũ "â15_1_48.dst".
function getGlyphUploadInfoFromFileName(fileName) {
    const name = (fileName || "").replace(/\.[^.]+$/, "");
    if (!name) return { character: "", sizeMm: 0, sizeLabel: "", underlays: [], baselineMm: 0 };
    const normalized = name.replace(/^glyph[-_]?/i, "").replace(/^char[-_]?/i, "");
    const baselineParts = normalized.split("#");
    const body = (baselineParts[0] || "").trim();
    const baselineMm = parseGlyphBaselineMmText(baselineParts.slice(1).join("#"));
    if (/^u\+[0-9a-f]{4,6}$/i.test(body)) return { character: String.fromCodePoint(parseInt(body.substring(2), 16)), sizeMm: 0, sizeLabel: "", underlays: [], baselineMm };
    if (/^[0-9a-f]{4,6}$/i.test(body)) return { character: String.fromCodePoint(parseInt(body, 16)), sizeMm: 0, sizeLabel: "", underlays: [], baselineMm };

    const character = Array.from(body)[0] || "";
    const rest = body.substring(character.length).replace(/^[-_\s]+/, "");
    // Khi có hậu tố baseline, phần sau ký tự là danh sách underlay chứ không còn là size label.
    if (baselineMm > 0) {
        return {
            character,
            sizeMm: 0,
            sizeLabel: "",
            underlays: parseGlyphUnderlaysText(rest),
            baselineMm,
        };
    }

    const match = rest.match(/^(\d+(?:\.\d+)?)(?:[_-](.*))?$/);
    const sizeMm = match ? Number(match[1]) : 0;
    const underlays = match ? parseGlyphUnderlaysText(match[2]) : [];
    return {
        character,
        sizeMm: Number.isNaN(sizeMm) ? 0 : sizeMm,
        sizeLabel: sizeMm && !Number.isNaN(sizeMm) ? `${sizeMm}mm` : "",
        underlays,
        baselineMm,
    };
}

// Hiển thị thông tin size/glyph trong table danh sách font.
function formatFontSummary(font) {
    const sizes = font.sizes || [];
    const glyphCount = sizes.reduce((total, size) => total + ((size.glyphs && size.glyphs.length) || size.glyphCount || 0), 0);
    return `${sizes.length || font.sizeCount || 0} size / ${glyphCount || font.glyphCount || 0} ký tự`;
}

const IMAGE_EXTENSIONS = [".png", ".jpg", ".jpeg", ".webp"];
// Font hiển thị đặt cùng cấp với các folder size trong bộ font thêu, vd Roboto_Thêu/Roboto-Medium.ttf
const FONT_FILE_EXTENSIONS = [".ttf", ".otf", ".woff", ".woff2"];

function getFileExtension(fileName) {
    const match = (fileName || "").match(/\.[^.]+$/);
    return match ? match[0].toLowerCase() : "";
}

function isDstFile(file) {
    return getFileExtension(file.name) === ".dst";
}

function isImageFile(file) {
    return IMAGE_EXTENSIONS.includes(getFileExtension(file.name));
}

function isFontFile(file) {
    return FONT_FILE_EXTENSIONS.includes(getFileExtension(file.name));
}

// Tên font-family suy từ tên file font, bỏ ký tự lạ để khai báo @font-face phía client không vỡ.
function createFontFamilyNameFromFileName(fileName) {
    return (fileName || "")
        .replace(/\.[^.]+$/, "")
        .replace(/[^a-zA-Z0-9_-]/g, "-")
        .replace(/-+/g, "-")
        .replace(/^-|-$/g, "");
}

function getRelativePathParts(file) {
    return ((file && file.webkitRelativePath) || (file && file.name) || "").split(/[\\/]/).filter(Boolean);
}

function getFolderNameFromFiles(files) {
    const fileWithPath = files.find((file) => file.webkitRelativePath);
    const parts = getRelativePathParts(fileWithPath);
    return parts.length > 1 ? parts[0] : "";
}

function getSizeMmFromFolderName(folderName) {
    const match = (folderName || "").match(/(\d+(?:\.\d+)?)\s*mm/i);
    if (!match) return 0;
    const sizeMm = Number(match[1]);
    return Number.isNaN(sizeMm) ? 0 : sizeMm;
}

function getSizeInfoFromFilePath(file) {
    const parts = getRelativePathParts(file);
    for (let i = parts.length - 2; i >= 1; i--) {
        const sizeMm = getSizeMmFromFolderName(parts[i]);
        if (sizeMm > 0) return { sizeMm, sizeLabel: `${sizeMm}mm` };
    }
    return { sizeMm: 0, sizeLabel: "" };
}

function createFontCodeFromFolderName(folderName) {
    return (folderName || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-zA-Z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "")
        .toUpperCase();
}

function getUploadedFileUrl(res) {
    if (!res || !res.data) return "";
    return res.data.imageUrl || res.data.url || "";
}

function roundMm(value) {
    return Number((Number(value) || 0).toFixed(2));
}

function getGlyphBaselineMm(glyph) {
    if (!glyph) return 0;
    const baselineMm = Number(glyph.baselineMm || glyph.BaselineMm) || 0;
    if (baselineMm > 0) return baselineMm;
    return Number(glyph.heightMm || glyph.HeightMm) || 0;
}

// Tính baseline preview trực tiếp từ glyph size đang test (giống filter khi gọi API compose),
// không phụ thuộc svgPreview.glyphs nên vẫn đúng khi font chỉ có DST, chưa có SVG.
function getPreviewBaselineMm(preview, sourceGlyphs) {
    const configuredBaselineMm = Number(preview.baselineMm || preview.BaselineMm) || 0;
    const usedGlyphs = (sourceGlyphs || []).filter((glyph) => glyph && glyph.isActive !== false && glyph.character && glyph.dstFileUrl);
    const maxGlyphBaselineMm = usedGlyphs.reduce((maxValue, glyph) => Math.max(maxValue, getGlyphBaselineMm(glyph)), 0);
    return Math.max(0, configuredBaselineMm, maxGlyphBaselineMm);
}

function isFailedApiResponse(value) {
    return value && typeof value === "object" && (value.success === false || value.Success === false || value.isSuccess === false || value.IsSuccess === false || value.succeeded === false || value.Succeeded === false);
}

function isGenericApiErrorMessage(message) {
    const normalized = String(message || "").trim().toLowerCase();
    return normalized === "lưu thất bại" || normalized === "lưu thất bại." || normalized === "save failed" || normalized === "save failed.";
}

function getApiErrorMessage(source, fallbackMessage) {
    if (!source) return fallbackMessage;
    if (typeof source === "string") return source;
    if (Array.isArray(source)) return source.map((item) => getApiErrorMessage(item, "")).filter(Boolean).join("; ") || fallbackMessage;
    if (typeof source !== "object") return fallbackMessage;

    const dataMessage = source.data && source.data !== source ? getApiErrorMessage(source.data, "") : "";

    const directMessage =
        source.message ||
        source.Message ||
        source.msg ||
        source.Msg ||
        source.errorMessage ||
        source.ErrorMessage ||
        source.resultMessage ||
        source.ResultMessage ||
        source.value ||
        source.Value ||
        source.reason ||
        source.Reason ||
        source.error ||
        source.Error ||
        source.detail ||
        source.Detail ||
        source.title ||
        source.Title;
    if (directMessage) {
        const parsedDirectMessage = getApiErrorMessage(directMessage, fallbackMessage);
        if (dataMessage && isGenericApiErrorMessage(parsedDirectMessage)) return dataMessage;
        return parsedDirectMessage;
    }
    if (dataMessage) return dataMessage;

    const errors = source.errors || source.Errors;
    if (errors) {
        if (Array.isArray(errors)) return getApiErrorMessage(errors, fallbackMessage);
        if (typeof errors === "object") {
            const messages = Object.keys(errors)
                .map((key) => getApiErrorMessage(errors[key], ""))
                .filter(Boolean);
            if (messages.length > 0) return messages.join("; ");
        }
    }

    if (source.response) return getApiErrorMessage(source.response, fallbackMessage);
    return fallbackMessage;
}

function getApiErrorFallbackDetail(source) {
    if (!source || typeof source !== "object") return "Backend không trả nội dung lỗi.";
    const status = source.status || source.Status || source.statusCode || source.StatusCode;
    const code = source.code || source.Code || source.errorCode || source.ErrorCode;
    const details = [];
    if (status) details.push(`Status: ${status}`);
    if (code) details.push(`Code: ${code}`);
    if (details.length > 0) return `Backend không trả message. ${details.join(" - ")}`;
    try {
        const compact = JSON.stringify(source);
        if (compact && compact !== "{}") return `Backend không trả message. Response: ${compact.substring(0, 300)}`;
    } catch {
        return "Backend không trả nội dung lỗi.";
    }
    return "Backend không trả nội dung lỗi.";
}

function parseDstHeaderNumber(header, key) {
    const value = header[key];
    if (value === undefined || value === null) return 0;
    const parsed = parseInt(String(value).trim(), 10);
    return Number.isNaN(parsed) ? 0 : parsed;
}

function parseDstHeaderMetadata(headerText) {
    const header = {};
    (headerText || "")
        .split("\r")
        .map((part) => part.trim())
        .forEach((part) => {
            if (part.length >= 3 && part[2] === ":") header[part.substring(0, 2)] = part.substring(3).trim();
        });

    const stitchCount = parseDstHeaderNumber(header, "ST");
    const widthMm = roundMm((parseDstHeaderNumber(header, "+X") + parseDstHeaderNumber(header, "-X")) / 10);
    const heightMm = roundMm((parseDstHeaderNumber(header, "+Y") + parseDstHeaderNumber(header, "-Y")) / 10);
    return { widthMm, heightMm, stitchCount };
}

function getDstBit(value, bit) {
    return (value >> bit) & 1;
}

function decodeDstDx(b0, b1, b2) {
    return getDstBit(b2, 2) * 81 + getDstBit(b2, 3) * -81 + getDstBit(b1, 2) * 27 + getDstBit(b1, 3) * -27 + getDstBit(b0, 2) * 9 + getDstBit(b0, 3) * -9 + getDstBit(b1, 0) * 3 + getDstBit(b1, 1) * -3 + getDstBit(b0, 0) * 1 + getDstBit(b0, 1) * -1;
}

function decodeDstDy(b0, b1, b2) {
    const y = getDstBit(b2, 5) * 81 + getDstBit(b2, 4) * -81 + getDstBit(b1, 5) * 27 + getDstBit(b1, 4) * -27 + getDstBit(b0, 5) * 9 + getDstBit(b0, 4) * -9 + getDstBit(b1, 7) * 3 + getDstBit(b1, 6) * -3 + getDstBit(b0, 7) * 1 + getDstBit(b0, 6) * -1;
    return -y;
}

function isDstEndCommand(b2) {
    return (b2 & 0b11110011) === 0b11110011;
}

function parseDstRecordMetadata(bytes) {
    let x = 0;
    let y = 0;
    let minX = 0;
    let maxX = 0;
    let minY = 0;
    let maxY = 0;
    let stitchCount = 0;

    for (let pos = 512; pos + 2 < bytes.length; pos += 3) {
        const b0 = bytes[pos];
        const b1 = bytes[pos + 1];
        const b2 = bytes[pos + 2];
        if (isDstEndCommand(b2)) break;
        x += decodeDstDx(b0, b1, b2);
        y += decodeDstDy(b0, b1, b2);
        minX = Math.min(minX, x);
        maxX = Math.max(maxX, x);
        minY = Math.min(minY, y);
        maxY = Math.max(maxY, y);
        stitchCount++;
    }

    return {
        widthMm: roundMm((maxX - minX) / 10),
        heightMm: roundMm((maxY - minY) / 10),
        stitchCount,
    };
}

async function getDstGlyphMetrics(file) {
    try {
        const buffer = await file.arrayBuffer();
        const bytes = new Uint8Array(buffer);
        const headerText = new TextDecoder("ascii").decode(bytes.slice(0, Math.min(512, bytes.length)));
        const headerMetrics = parseDstHeaderMetadata(headerText);
        if (headerMetrics.widthMm > 0 && headerMetrics.heightMm > 0 && headerMetrics.stitchCount > 0) return headerMetrics;

        const recordMetrics = parseDstRecordMetadata(bytes);
        return {
            widthMm: headerMetrics.widthMm || recordMetrics.widthMm,
            heightMm: headerMetrics.heightMm || recordMetrics.heightMm,
            stitchCount: headerMetrics.stitchCount || recordMetrics.stitchCount,
        };
    } catch {
        return { widthMm: 0, heightMm: 0, stitchCount: 0 };
    }
}

// Màn quản lý font thêu, dùng chung service Embroidery để sau này nối backend cùng controller hiện tại.
function EmbroideryFontManagement() {
    const [datas, setDatas] = useState([]);
    const [paging, setPaging] = useState(DEFAULT_PAGING);
    const [isBusy, setIsBusy] = useState(false);
    const [isShowModal, setIsShowModal] = useState(false);
    const [selectedId, setSelectedId] = useState(0);

    useEffect(() => {
        _getData(DEFAULT_PAGING);
    }, []);

    // Lấy danh sách font từ API paging, dùng cho load đầu trang và refresh sau khi lưu/xóa.
    async function _getData(newPaging) {
        const requestPaging = newPaging || paging || DEFAULT_PAGING;
        setIsBusy(true);
        const [err, data] = await EmbroideryServices.GetFonts(requestPaging);
        if (!err && data) {
            setDatas(data.listObjects || []);
            setPaging(data.paging || requestPaging);
        } else {
            setDatas([]);
            setPaging(requestPaging);
            Notify(NOTIFY.WARNING, "Có lỗi", "Không tải được danh sách font thêu.");
        }
        setIsBusy(false);
    }

    // Mở modal thêm mới font thêu.
    function _openCreateModal() {
        setSelectedId(0);
        setIsShowModal(true);
    }

    // Mở modal chỉnh sửa font thêu theo id được chọn.
    function _openEditModal(id) {
        setSelectedId(id);
        setIsShowModal(true);
    }

    // Đóng modal và reset id đang chọn.
    function _closeModal() {
        setSelectedId(0);
        setIsShowModal(false);
    }

    // Xóa mềm font thêu sau khi admin xác nhận.
    function _handleDelete(id) {
        ConfirmDialog("Xác nhận xóa?", "Xin vui lòng xác nhận để xóa bộ font thêu này", () => {
            return new Promise(async (resolve, reject) => {
                const [err, data] = await EmbroideryServices.DeleteFont(id);
                if (!err && data) {
                    _getData(paging);
                    resolve({ title: "Thành công", msg: "Xóa thành công." });
                    return;
                }
                Notify(NOTIFY.WARNING, "Có lỗi", "Có lỗi xảy ra trong quá trình xóa.");
                reject({ title: "Thất bại", msg: "Vui lòng thử lại sau." });
            });
        });
    }

    // Render nút thêm font theo style CommonTable hiện có.
    function _renderAction() {
        return (
            <button className="btn btn-custom my-2" onClick={_openCreateModal}>
                <i className="fa fa-plus mr-2"></i>
                Thêm font thêu
            </button>
        );
    }

    // Render header table danh sách font thêu.
    function _renderHeader() {
        return (
            <thead>
                <tr className="text-center">
                    <th style={{ minWidth: 180 }}>Tên font</th>
                    <th style={{ minWidth: 160 }}>Text hiển thị</th>
                    <th style={{ width: 110 }}>Preview</th>
                    <th style={{ minWidth: 140 }}>Mã font</th>
                    <th style={{ minWidth: 180 }}>Size / ký tự</th>
                    <th style={{ minWidth: 120 }}>Trạng thái</th>
                    <th style={{ width: 180 }}>Hành động</th>
                </tr>
            </thead>
        );
    }

    // Render body table, ưu tiên summary từ API nhưng vẫn đọc được shape chi tiết khi backend trả sizes.
    function _renderBody() {
        return (
            <tbody>
                {datas && datas.length > 0 ? (
                    datas.map((item) => (
                        <tr key={item.id}>
                            <td className="vertical-align-middle">
                                <div className="font-weight-bold">{item.name}</div>
                                {item.description && <div className="text-muted">{item.description}</div>}
                            </td>
                            <td className="vertical-align-middle">{item.text || item.name || "-"}</td>
                            <td className="text-center vertical-align-middle">{item.previewImageUrl ? <img src={item.previewImageUrl} alt={item.name} className="emb-font-preview" /> : <span className="text-muted">-</span>}</td>
                            <td className="text-center vertical-align-middle">{item.code || "-"}</td>
                            <td className="text-center vertical-align-middle">{formatFontSummary(item)}</td>
                            <td className="text-center vertical-align-middle">
                                <span className={item.isActive === false ? "badge badge-secondary" : "badge badge-success"}>{item.isActive === false ? "Tắt" : "Đang bật"}</span>
                            </td>
                            <td className="text-center vertical-align-middle">
                                <button className="btn btn-custom m-1" onClick={() => _openEditModal(item.id)}>
                                    Sửa
                                </button>
                                <button className="btn btn-danger m-1" onClick={() => _handleDelete(item.id)}>
                                    Xóa
                                </button>
                            </td>
                        </tr>
                    ))
                ) : (
                    <tr>
                        <td colSpan={7} className="text-center">
                            Không có dữ liệu
                        </td>
                    </tr>
                )}
            </tbody>
        );
    }

    return (
        <React.Fragment>
            <Breadcrumbs currentPage="Quản lý font thêu" />
            <Loading show={isBusy} msg="Đang tải dữ liệu..." />
            <PageWrapper>
                <CommonTable data={datas} paging={paging} searchBasic freeSize renderAction={_renderAction} renderHeader={_renderHeader} renderBody={_renderBody} onFilter={_getData} />
            </PageWrapper>
            {isShowModal && (
                <FontUploadModal
                    id={selectedId}
                    isShowModal={isShowModal}
                    onClose={_closeModal}
                    saveSucceed={() => {
                        _closeModal();
                        _getData(paging);
                    }}
                />
            )}
        </React.Fragment>
    );
}

export default EmbroideryFontManagement;

// Modal quản lý chi tiết font thêu gồm thông tin font, danh sách size và glyph DST.
function FontUploadModal({ id, isShowModal, onClose, saveSucceed }) {
    const [font, setFont] = useState(createEmptyFont());
    const [activeSizeIndex, setActiveSizeIndex] = useState(0);
    const [isBusy, setIsBusy] = useState(false);
    const [folderUploadSummary, setFolderUploadSummary] = useState("");
    const [fontConfigPreview, setFontConfigPreview] = useState(null);
    const [underlayTestResult, setUnderlayTestResult] = useState(null);
    const [isShowPreviewModal, setIsShowPreviewModal] = useState(false);

    useEffect(() => {
        if (id > 0) {
            _getData(id);
            return;
        }
        setFont(createEmptyFont());
        setActiveSizeIndex(0);
        setFolderUploadSummary("");
        setFontConfigPreview(null);
        setUnderlayTestResult(null);
        setIsShowPreviewModal(false);
    }, [id]);

    useEffect(() => {
        if (!fontConfigPreview && isShowPreviewModal) setIsShowPreviewModal(false);
    }, [fontConfigPreview, isShowPreviewModal]);

    const activeSize = font.sizes[activeSizeIndex];
    const glyphSummary = useMemo(() => {
        const glyphs = activeSize && activeSize.glyphs ? activeSize.glyphs : [];
        const uploaded = glyphs.filter((glyph) => glyph.dstFileUrl).length;
        return `${uploaded}/${glyphs.length} ký tự đã upload`;
    }, [activeSize]);

    // Lấy chi tiết font để bind modal chỉnh sửa, fallback về size mặc định nếu font chưa có size.
    async function _getData(fontId) {
        setIsBusy(true);
        const [err, data] = await EmbroideryServices.GetFontById(fontId);
        if (!err && data) {
            setFont({
                ...createEmptyFont(),
                ...data,
                text: data.text || data.name || "",
                sizes:
                    data.sizes && data.sizes.length > 0
                        ? data.sizes.map((size) => ({
                              ...createFontSize(size.sizeMm),
                              ...size,
                              glyphs: (size.glyphs || []).map((glyph) => ({
                                  ...createEmptyGlyph(glyph.character),
                                  ...glyph,
                                  baselineMm: getDefaultGlyphBaselineMm(glyph.heightMm, glyph.baselineMm),
                                  underlayText: formatGlyphUnderlays(glyph.underlays),
                                  underlays: glyph.underlays || [],
                              })),
                          }))
                        : DEFAULT_FONT_SIZES_MM.map((size) => createFontSize(size)),
            });
            setActiveSizeIndex(0);
            setFontConfigPreview(null);
            setUnderlayTestResult(null);
        } else {
            Notify(NOTIFY.WARNING, "Có lỗi", "Không tải được thông tin font thêu.");
        }
        setIsBusy(false);
    }

    // Cập nhật field cấp font, giữ state immutable để React render ổn định.
    function _handleChangeFont(field, value, isNumber) {
        setFontConfigPreview(null);
        setUnderlayTestResult(null);
        setFont((prev) => {
            const nextValue = isNumber ? normalizeNumber(value) : value;
            const next = { ...prev, [field]: nextValue };
            // Khi admin đổi tên font lần đầu, text hiển thị sẽ tự đi theo name cho tới khi được nhập tay.
            if (field === "name" && (!prev.text || prev.text === prev.name)) next.text = value;
            return next;
        });
    }

    // Cập nhật field cấp size đang chọn.
    function _handleChangeSize(sizeIndex, field, value, isNumber) {
        setFontConfigPreview(null);
        setUnderlayTestResult(null);
        setFont((prev) => {
            const sizes = [...prev.sizes];
            sizes[sizeIndex] = { ...sizes[sizeIndex], [field]: isNumber ? normalizeNumber(value) : value };
            return { ...prev, sizes };
        });
    }

    // Cập nhật một field của glyph trong size đang chọn.
    function _handleChangeGlyph(sizeIndex, glyphIndex, field, value, isNumber) {
        setFontConfigPreview(null);
        setUnderlayTestResult(null);
        setFont((prev) => {
            const sizes = [...prev.sizes];
            const glyphs = [...(sizes[sizeIndex].glyphs || [])];
            const nextGlyph = { ...glyphs[glyphIndex], [field]: isNumber ? normalizeNumber(value) : value };
            if (field === "character") nextGlyph.unicodeCodePoint = value ? value.codePointAt(0) : 0;
            glyphs[glyphIndex] = nextGlyph;
            sizes[sizeIndex] = { ...sizes[sizeIndex], glyphs };
            return { ...prev, sizes };
        });
    }

    // Upload ảnh preview cấp font, chỉ phục vụ admin nhận diện nhanh bộ font.
    // Cập nhật underlay theo chuỗi "start_end, start_end" để admin sửa được khi tên file parse chưa đúng.
    function _handleChangeGlyphUnderlays(sizeIndex, glyphIndex, value) {
        setFontConfigPreview(null);
        setUnderlayTestResult(null);
        setFont((prev) => {
            const sizes = [...prev.sizes];
            const glyphs = [...(sizes[sizeIndex].glyphs || [])];
            glyphs[glyphIndex] = { ...glyphs[glyphIndex], underlayText: value, underlays: parseGlyphUnderlaysText(value) };
            sizes[sizeIndex] = { ...sizes[sizeIndex], glyphs };
            return { ...prev, sizes };
        });
    }

    async function _handleUploadFontPreview(e) {
        const input = e.target;
        const file = input.files[0];
        if (!file) return;
        setIsBusy(true);
        const res = await Request.UploadImage(file);
        setIsBusy(false);
        if (res && res.data) {
            setFont((prev) => ({ ...prev, previewImageUrl: res.data.imageUrl || res.data.url }));
        } else {
            Notify(NOTIFY.ERROR, NOTIFY.ERROR, "Upload preview font thất bại");
        }
        input.value = "";
    }

    // Upload file font hiển thị (.ttf/.otf/.woff) của bộ chữ thêu. File này KHÔNG tham gia ghép DST,
    // chỉ để editor bên ecommerce đăng ký @font-face và cho textarea nhập liệu giống hình dáng chữ thêu.
    async function _handleUploadTrueTypeFont(e) {
        const input = e.target;
        const file = input.files[0];
        if (!file) return;
        setIsBusy(true);
        const res = await Request.UploadDocument(file);
        setIsBusy(false);
        const url = getUploadedFileUrl(res);
        if (url) {
            setFont((prev) => ({
                ...prev,
                trueTypeFontFileUrl: url,
                // Chưa đặt tên font-family thì suy từ tên file, bỏ ký tự lạ để khai báo CSS không vỡ.
                fontFamilyName: prev.fontFamilyName || file.name.replace(/\.[^.]+$/, "").replace(/[^a-zA-Z0-9_-]/g, "-"),
            }));
        } else {
            Notify(NOTIFY.ERROR, NOTIFY.ERROR, "Upload file font thất bại");
        }
        input.value = "";
    }

    // Upload ảnh preview cấp size, dùng khi muốn hiển thị mẫu chữ của riêng size đó.
    async function _handleUploadSizePreview(sizeIndex, e) {
        const input = e.target;
        const file = input.files[0];
        if (!file) return;
        setIsBusy(true);
        const res = await Request.UploadImage(file);
        setIsBusy(false);
        if (res && res.data) {
            _handleChangeSize(sizeIndex, "previewImageUrl", res.data.imageUrl || res.data.url);
        } else {
            Notify(NOTIFY.ERROR, NOTIFY.ERROR, "Upload preview size thất bại");
        }
        input.value = "";
    }

    // Upload file DST cho một glyph, lưu url và tên file vào đúng dòng ký tự.
    async function _handleUploadGlyphDst(sizeIndex, glyphIndex, e) {
        const input = e.target;
        const file = input.files[0];
        if (!file) return;
        setFontConfigPreview(null);
        setUnderlayTestResult(null);
        setIsBusy(true);
        const dstMetrics = await getDstGlyphMetrics(file);
        const res = await Request.UploadDocument(file);
        setIsBusy(false);
        if (res && res.data) {
            const uploadInfo = getGlyphUploadInfoFromFileName(file.name);
            setFont((prev) => {
                const sizes = [...prev.sizes];
                const glyphs = [...(sizes[sizeIndex].glyphs || [])];
                const character = uploadInfo.character || glyphs[glyphIndex].character || "";
                glyphs[glyphIndex] = {
                    ...glyphs[glyphIndex],
                    character,
                    unicodeCodePoint: character ? character.codePointAt(0) : 0,
                    dstFileUrl: res.data.url,
                    dstFileName: file.name,
                    widthMm: dstMetrics.widthMm || glyphs[glyphIndex].widthMm || 0,
                    heightMm: dstMetrics.heightMm || glyphs[glyphIndex].heightMm || 0,
                    baselineMm: uploadInfo.baselineMm || getDefaultGlyphBaselineMm(dstMetrics.heightMm || glyphs[glyphIndex].heightMm, glyphs[glyphIndex].baselineMm),
                    stitchCount: dstMetrics.stitchCount || glyphs[glyphIndex].stitchCount || 0,
                    fileSizeByte: file.size || glyphs[glyphIndex].fileSizeByte || 0,
                    underlayText: uploadInfo.underlays.length > 0 ? formatGlyphUnderlays(uploadInfo.underlays) : glyphs[glyphIndex].underlayText || formatGlyphUnderlays(glyphs[glyphIndex].underlays),
                    underlays: uploadInfo.underlays.length > 0 ? uploadInfo.underlays : glyphs[glyphIndex].underlays || [],
                };
                if (uploadInfo.sizeMm && (!sizes[sizeIndex].sizeMm || Number(sizes[sizeIndex].sizeMm) === uploadInfo.sizeMm)) {
                    sizes[sizeIndex] = { ...sizes[sizeIndex], sizeMm: uploadInfo.sizeMm, sizeLabel: uploadInfo.sizeLabel };
                }
                sizes[sizeIndex] = { ...sizes[sizeIndex], glyphs };
                return { ...prev, sizes };
            });
        } else {
            Notify(NOTIFY.ERROR, NOTIFY.ERROR, "Upload file .DST thất bại");
        }
        input.value = "";
    }

    // Đọc một folder font dạng Block2: root chứa PNG ký tự, thư mục con theo size chứa file DST.
    async function _handleUploadFontFolder(e) {
        const input = e.target;
        const files = Array.from(input.files || []);
        if (files.length === 0) return;
        setFontConfigPreview(null);
        setUnderlayTestResult(null);

        const dstFiles = files.filter(isDstFile);
        const imageFiles = files.filter(isImageFile);
        if (dstFiles.length === 0) {
            Notify(NOTIFY.WARNING, "Chú ý", "Folder font phải có ít nhất một file .DST.");
            input.value = "";
            return;
        }

        const folderName = getFolderNameFromFiles(files);
        const imageByCharacter = {};
        const failedFiles = [];
        setIsBusy(true);
        try {
            // File font hiển thị (nếu bộ font có kèm) upload luôn, admin không phải chọn lại tay.
            let uploadedFont = null;
            const fontFile = files.find(isFontFile);
            if (fontFile) {
                try {
                    const res = await Request.UploadDocument(fontFile);
                    const url = getUploadedFileUrl(res);
                    if (url) uploadedFont = { url, familyName: createFontFamilyNameFromFileName(fontFile.name) };
                    else failedFiles.push(fontFile.name);
                } catch {
                    failedFiles.push(fontFile.name);
                }
            }

            for (let i = 0; i < imageFiles.length; i++) {
                const file = imageFiles[i];
                const uploadInfo = getGlyphUploadInfoFromFileName(file.name);
                if (!uploadInfo.character) continue;
                try {
                    const res = await Request.UploadImage(file);
                    const url = getUploadedFileUrl(res);
                    if (!url) {
                        failedFiles.push(file.name);
                        continue;
                    }
                    const pathSizeInfo = getSizeInfoFromFilePath(file);
                    const imageInfo = { url, fileName: file.name };
                    imageByCharacter[uploadInfo.character] = imageInfo;
                    if (pathSizeInfo.sizeMm > 0) imageByCharacter[`${pathSizeInfo.sizeMm}|${uploadInfo.character}`] = imageInfo;
                } catch {
                    failedFiles.push(file.name);
                }
            }

            const sizeMap = {};
            for (let i = 0; i < dstFiles.length; i++) {
                const file = dstFiles[i];
                const uploadInfo = getGlyphUploadInfoFromFileName(file.name);
                const pathSizeInfo = getSizeInfoFromFilePath(file);
                const sizeMm = pathSizeInfo.sizeMm || uploadInfo.sizeMm;
                const character = uploadInfo.character;
                if (!sizeMm || !character) {
                    failedFiles.push(file.name);
                    continue;
                }

                try {
                    const dstMetrics = await getDstGlyphMetrics(file);
                    const res = await Request.UploadDocument(file);
                    const url = getUploadedFileUrl(res);
                    if (!url) {
                        failedFiles.push(file.name);
                        continue;
                    }
                    const imageInfo = imageByCharacter[`${sizeMm}|${character}`] || imageByCharacter[character] || {};
                    if (!sizeMap[sizeMm]) sizeMap[sizeMm] = { sizeMm, sizeLabel: pathSizeInfo.sizeLabel || uploadInfo.sizeLabel || `${sizeMm}mm`, glyphs: [] };
                    sizeMap[sizeMm].glyphs.push({
                        ...createEmptyGlyph(character),
                        dstFileUrl: url,
                        dstFileName: file.name,
                        pngFileUrl: imageInfo.url || "",
                        pngFileName: imageInfo.fileName || "",
                        widthMm: dstMetrics.widthMm || 0,
                        heightMm: dstMetrics.heightMm || 0,
                        baselineMm: uploadInfo.baselineMm || getDefaultGlyphBaselineMm(dstMetrics.heightMm, 0),
                        stitchCount: dstMetrics.stitchCount || 0,
                        fileSizeByte: file.size || 0,
                        underlayText: formatGlyphUnderlays(uploadInfo.underlays),
                        underlays: uploadInfo.underlays,
                    });
                } catch {
                    failedFiles.push(file.name);
                }
            }

            const parsedSizes = Object.values(sizeMap)
                .sort((a, b) => Number(a.sizeMm) - Number(b.sizeMm))
                .map((size, sizeIndex, allSizes) => {
                    const defaultIndex = allSizes.findIndex((item) => Number(item.sizeMm) === 15);
                    const glyphs = size.glyphs.sort((a, b) => (a.unicodeCodePoint || 0) - (b.unicodeCodePoint || 0)).map((glyph, glyphIndex) => ({ ...glyph, sortOrder: glyphIndex }));
                    return {
                        ...createFontSize(size.sizeMm),
                        sizeLabel: size.sizeLabel,
                        sortOrder: sizeIndex,
                        isDefault: defaultIndex >= 0 ? sizeIndex === defaultIndex : sizeIndex === 0,
                        glyphs,
                    };
                });

            if (parsedSizes.length === 0) {
                Notify(NOTIFY.ERROR, NOTIFY.ERROR, "Không đọc được dữ liệu font từ folder đã chọn.");
                return;
            }

            const firstImage = Object.values(imageByCharacter).find((item) => item && item.url);
            const fontCode = createFontCodeFromFolderName(folderName);
            const uploadedGlyphCount = parsedSizes.reduce((total, size) => total + (size.glyphs || []).length, 0);
            setFont((prev) => ({
                ...prev,
                name: folderName || prev.name,
                text: !prev.text || prev.text === prev.name ? folderName || prev.text || prev.name : prev.text,
                code: fontCode || prev.code,
                previewImageUrl: prev.previewImageUrl || (firstImage && firstImage.url) || "",
                // Folder có file font thì ghi đè, vì đây là bộ font vừa chọn chứ không phải bộ cũ.
                trueTypeFontFileUrl: uploadedFont ? uploadedFont.url : prev.trueTypeFontFileUrl,
                fontFamilyName: uploadedFont ? uploadedFont.familyName : prev.fontFamilyName,
                sizes: parsedSizes,
            }));
            setActiveSizeIndex(0);
            setFolderUploadSummary(
                `${folderName || "Folder"}: ${parsedSizes.length} size / ${uploadedGlyphCount} file DST / ${Object.keys(imageByCharacter).filter((key) => key.indexOf("|") < 0).length} ảnh preview / ${
                    uploadedFont ? `font ${uploadedFont.familyName}` : "chưa có file font"
                }`
            );
            Notify(NOTIFY.SUCCESS, NOTIFY.SUCCESS, failedFiles.length > 0 ? `Đã đọc folder, ${failedFiles.length} file lỗi hoặc bỏ qua.` : "Đã đọc folder font thành công.");
        } finally {
            setIsBusy(false);
            input.value = "";
        }
    }

    // Upload nhiều DST một lượt, tự lấy ký tự từ tên file để tạo/cập nhật glyph trong size đang chọn.
    async function _handleUploadManyGlyphDst(sizeIndex, e) {
        const input = e.target;
        const files = Array.from(input.files || []);
        if (files.length === 0) return;
        setFontConfigPreview(null);
        setUnderlayTestResult(null);
        setIsBusy(true);
        const uploadedGlyphs = [];
        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            const dstMetrics = await getDstGlyphMetrics(file);
            const res = await Request.UploadDocument(file);
            if (res && res.data) {
                const uploadInfo = getGlyphUploadInfoFromFileName(file.name);
                uploadedGlyphs.push({
                    ...createEmptyGlyph(uploadInfo.character),
                    dstFileUrl: res.data.url,
                    dstFileName: file.name,
                    widthMm: dstMetrics.widthMm || 0,
                    heightMm: dstMetrics.heightMm || 0,
                    baselineMm: uploadInfo.baselineMm || getDefaultGlyphBaselineMm(dstMetrics.heightMm, 0),
                    stitchCount: dstMetrics.stitchCount || 0,
                    fileSizeByte: file.size || 0,
                    sortOrder: i,
                    underlayText: formatGlyphUnderlays(uploadInfo.underlays),
                    underlays: uploadInfo.underlays,
                    uploadSizeMm: uploadInfo.sizeMm,
                    uploadSizeLabel: uploadInfo.sizeLabel,
                });
            }
        }
        setIsBusy(false);
        if (uploadedGlyphs.length === 0) {
            Notify(NOTIFY.ERROR, NOTIFY.ERROR, "Upload danh sách .DST thất bại");
            input.value = "";
            return;
        }
        setFont((prev) => {
            const sizes = [...prev.sizes];
            uploadedGlyphs.forEach((newGlyph) => {
                let targetSizeIndex = newGlyph.uploadSizeMm ? sizes.findIndex((size) => Number(size.sizeMm) === Number(newGlyph.uploadSizeMm)) : sizeIndex;
                if (targetSizeIndex < 0) {
                    sizes.push(createFontSize(newGlyph.uploadSizeMm));
                    targetSizeIndex = sizes.length - 1;
                }
                const targetSize = sizes[targetSizeIndex];
                const glyphs = [...(targetSize.glyphs || [])];
                const idx = glyphs.findIndex((glyph) => glyph.character === newGlyph.character && newGlyph.character);
                const { uploadSizeMm, uploadSizeLabel, ...glyphPayload } = newGlyph;
                if (idx >= 0) glyphs[idx] = { ...glyphs[idx], ...glyphPayload, id: glyphs[idx].id || 0 };
                else glyphs.push(glyphPayload);
                sizes[targetSizeIndex] = { ...targetSize, sizeLabel: newGlyph.uploadSizeLabel || targetSize.sizeLabel, glyphs };
            });
            return { ...prev, sizes };
        });
        input.value = "";
    }

    // Thêm size mới vào bộ font, mặc định nhảy sang tab size vừa thêm.
    function _handleAddSize() {
        setFontConfigPreview(null);
        setUnderlayTestResult(null);
        setFont((prev) => {
            const sizes = [...prev.sizes, createFontSize("")];
            setActiveSizeIndex(sizes.length - 1);
            return { ...prev, sizes };
        });
    }

    // Xóa size đang chọn khỏi font, giữ lại ít nhất một size để form không bị trống.
    function _handleRemoveSize(sizeIndex) {
        if (font.sizes.length <= 1) {
            Notify(NOTIFY.WARNING, "Chú ý", "Font thêu phải có ít nhất một size");
            return;
        }
        setFontConfigPreview(null);
        setUnderlayTestResult(null);
        setFont((prev) => {
            const sizes = prev.sizes.filter((_, idx) => idx !== sizeIndex);
            setActiveSizeIndex(sizeIndex >= sizes.length ? sizes.length - 1 : sizeIndex);
            return { ...prev, sizes };
        });
    }

    // Thêm nhanh nhóm ký tự phổ biến vào size đang chọn để admin upload DST dần.
    function _handleAddGlyphGroup(sizeIndex, groupKey) {
        const chars = DEFAULT_GLYPH_GROUPS[groupKey].split("");
        setFontConfigPreview(null);
        setUnderlayTestResult(null);
        setFont((prev) => {
            const sizes = [...prev.sizes];
            const glyphs = [...(sizes[sizeIndex].glyphs || [])];
            const existing = new Set(glyphs.map((glyph) => glyph.character));
            chars.forEach((character) => {
                if (!existing.has(character)) glyphs.push(createEmptyGlyph(character));
            });
            sizes[sizeIndex] = { ...sizes[sizeIndex], glyphs };
            return { ...prev, sizes };
        });
    }

    // Thêm một dòng glyph rỗng để admin nhập ký tự thủ công.
    function _handleAddGlyph(sizeIndex) {
        setFontConfigPreview(null);
        setUnderlayTestResult(null);
        setFont((prev) => {
            const sizes = [...prev.sizes];
            sizes[sizeIndex] = { ...sizes[sizeIndex], glyphs: [...(sizes[sizeIndex].glyphs || []), createEmptyGlyph()] };
            return { ...prev, sizes };
        });
    }

    // Xóa glyph khỏi size hiện tại.
    function _handleRemoveGlyph(sizeIndex, glyphIndex) {
        setFontConfigPreview(null);
        setUnderlayTestResult(null);
        setFont((prev) => {
            const sizes = [...prev.sizes];
            const glyphs = [...(sizes[sizeIndex].glyphs || [])];
            glyphs.splice(glyphIndex, 1);
            sizes[sizeIndex] = { ...sizes[sizeIndex], glyphs };
            return { ...prev, sizes };
        });
    }

    // Validate và lưu font thêu, backend sẽ quyết định create/update theo id.
    // Gọi API test cấu hình size hiện tại để compose DST và dựng preview 2D trước khi admin lưu font.
    // Kiểm tra underlay từng glyph theo stitchCount hiện tại để admin biết ký tự nào thiếu/sai range.
    async function _handleTestUnderlays() {
        const glyphs = activeSize && activeSize.glyphs ? activeSize.glyphs.filter((glyph) => glyph && glyph.isActive !== false && glyph.character) : [];
        if (glyphs.length === 0) {
            Notify(NOTIFY.WARNING, "Chú ý", "Chưa có ký tự active để test underlay.");
            return;
        }

        const rows = glyphs.map((glyph) => {
            const rawText = glyph.underlayText !== undefined ? glyph.underlayText : formatGlyphUnderlays(glyph.underlays);
            const parts = (rawText || "")
                .split(/[,\s;]+/)
                .map((part) => part.trim())
                .filter(Boolean);
            const stitchCount = Number(glyph.stitchCount) || 0;
            const messages = [];
            const ranges = [];
            if (parts.length === 0) messages.push("Chưa có underlay");
            parts.forEach((part) => {
                const match = part.match(/^(\d+)[_-](\d+)$/);
                if (!match) {
                    messages.push(`Sai format: ${part}`);
                    return;
                }
                const start = Number(match[1]);
                const end = Number(match[2]);
                if (start <= 0) messages.push(`Start phải > 0: ${part}`);
                if (end < start) messages.push(`End nhỏ hơn start: ${part}`);
                if (stitchCount > 0 && end > stitchCount) messages.push(`Vượt stitch ${stitchCount}: ${part}`);
                ranges.push({ start, end, text: `${start}_${end}` });
            });
            ranges.sort((a, b) => a.start - b.start);
            for (let i = 1; i < ranges.length; i++) {
                if (ranges[i].start <= ranges[i - 1].end) messages.push(`Overlap: ${ranges[i - 1].text} và ${ranges[i].text}`);
            }
            const hasError = messages.some((msg) => msg.indexOf("Sai format") >= 0 || msg.indexOf("End nhỏ") >= 0 || msg.indexOf("Vượt stitch") >= 0 || msg.indexOf("Overlap") >= 0 || msg.indexOf("Start") >= 0);
            const status = hasError ? "error" : messages.length > 0 ? "warning" : "ok";
            return {
                character: glyph.character,
                fileName: glyph.dstFileName,
                stitchCount,
                underlayText: ranges.length > 0 ? ranges.map((range) => range.text).join(", ") : rawText || "-",
                status,
                messages,
            };
        });

        const result = {
            total: rows.length,
            ok: rows.filter((row) => row.status === "ok").length,
            warning: rows.filter((row) => row.status === "warning").length,
            error: rows.filter((row) => row.status === "error").length,
            rows,
        };
        setUnderlayTestResult(result);
        if (result.error > 0) Notify(NOTIFY.ERROR, NOTIFY.ERROR, `Có ${result.error} ký tự underlay lỗi.`);
        else if (result.warning > 0) Notify(NOTIFY.WARNING, "Chú ý", `Có ${result.warning} ký tự chưa có underlay.`);
        else Notify(NOTIFY.SUCCESS, NOTIFY.SUCCESS, "Underlay của size hiện tại hợp lệ.");
        if (result.error > 0) return;
        if (result.ok <= 0) {
            setFontConfigPreview(null);
            return;
        }
        setIsBusy(true);
        const [err, data] = await EmbroideryServices.PreviewFontUnderlayConfig({ font, sizeIndex: activeSizeIndex });
        setIsBusy(false);
        if (!err && data) {
            setFontConfigPreview({ ...data, previewTitle: "Preview test underlay", downloadText: "Tải DST underlay test" });
            return;
        }
        setFontConfigPreview(null);
        Notify(NOTIFY.ERROR, NOTIFY.ERROR, "Tạo DST test underlay thất bại.");
    }

    async function _handlePreviewFontConfig() {
        const glyphs = activeSize && activeSize.glyphs ? activeSize.glyphs : [];
        const validGlyphs = glyphs.filter((glyph) => glyph && glyph.isActive !== false && glyph.character && glyph.dstFileUrl);
        if (!activeSize || validGlyphs.length === 0) {
            Notify(NOTIFY.WARNING, "Chú ý", "Vui lòng upload ít nhất một ký tự .DST để test cấu hình.");
            return;
        }
        setIsBusy(true);
        const [err, data] = await EmbroideryServices.PreviewFontConfig({ font, sizeIndex: activeSizeIndex });
        setIsBusy(false);
        if (!err && data) {
            setFontConfigPreview({ ...data, previewTitle: "Preview test cấu hình", downloadText: "Tải DST test" });
            Notify(NOTIFY.SUCCESS, NOTIFY.SUCCESS, "Đã tạo preview test font thêu.");
            return;
        }
        setFontConfigPreview(null);
        Notify(NOTIFY.ERROR, NOTIFY.ERROR, "Test cấu hình font thêu thất bại.");
    }

    async function _handleSave() {
        if (!font.name || !font.name.trim()) {
            Notify(NOTIFY.WARNING, "Chú ý", "Vui lòng nhập tên font thêu.");
            return;
        }
        const validSizes = (font.sizes || []).filter((size) => Number(size.sizeMm) > 0);
        if (validSizes.length === 0) {
            Notify(NOTIFY.WARNING, "Chú ý", "Vui lòng cấu hình ít nhất một size font.");
            return;
        }
        for (let i = 0; i < validSizes.length; i++) {
            const duplicated = new Set();
            const glyphs = validSizes[i].glyphs || [];
            for (let j = 0; j < glyphs.length; j++) {
                const character = glyphs[j].character;
                if (!character) continue;
                if (duplicated.has(character)) {
                    setActiveSizeIndex(i);
                    Notify(NOTIFY.WARNING, "Chú ý", `Size ${validSizes[i].sizeMm}mm đang bị trùng ký tự "${character}".`);
                    return;
                }
                duplicated.add(character);
            }
        }

        const payload = {
            ...font,
            name: font.name.trim(),
            text: (font.text || font.name || "").trim(),
            code: (font.code || "").trim(),
            sizes: validSizes.map((size, sizeIndex) => ({
                ...size,
                sizeMm: Number(size.sizeMm),
                sizeLabel: size.sizeLabel || `${size.sizeMm}mm`,
                sortOrder: size.sortOrder || sizeIndex,
                glyphs: (size.glyphs || [])
                    .filter((glyph) => glyph.character)
                    .map((glyph, glyphIndex) => {
                        const glyphPayload = { ...glyph };
                        delete glyphPayload.advanceMm;
                        delete glyphPayload.leftBearingMm;
                        delete glyphPayload.rightBearingMm;
                        delete glyphPayload.baselineOffsetMm;
                        delete glyphPayload.underlayText;
                        delete glyphPayload.uploadSizeMm;
                        delete glyphPayload.uploadSizeLabel;
                        return {
                            ...glyphPayload,
                            character: glyph.character,
                            unicodeCodePoint: glyph.unicodeCodePoint || glyph.character.codePointAt(0),
                            widthMm: Number(glyph.widthMm) || 0,
                            heightMm: Number(glyph.heightMm) || 0,
                            baselineMm: Number(glyph.baselineMm) || 0,
                            stitchCount: Number(glyph.stitchCount) || 0,
                            sortOrder: glyph.sortOrder || glyphIndex,
                            underlays: (glyph.underlays || [])
                                .filter((underlay) => Number(underlay.startStitchIndex) > 0 && Number(underlay.endStitchIndex) >= Number(underlay.startStitchIndex))
                                .map((underlay, underlayIndex) => ({
                                    ...underlay,
                                    underlayIndex,
                                    startStitchIndex: Number(underlay.startStitchIndex),
                                    endStitchIndex: Number(underlay.endStitchIndex),
                                    isActive: underlay.isActive !== false,
                                    sortOrder: underlay.sortOrder || underlayIndex,
                                })),
                        };
                    }),
            })),
        };
        setIsBusy(true);
        const [err, data] = await EmbroideryServices.CreateUpdateFont(payload);
        setIsBusy(false);
        if (!err && data && !isFailedApiResponse(data)) {
            Notify(NOTIFY.SUCCESS, NOTIFY.SUCCESS, "Lưu thành công");
            saveSucceed();
            return;
        }
        // Backend trả message cụ thể (vd trùng tên font) qua err hoặc data tùy status code, ưu tiên hiển thị thay vì text chung chung.
        const errorSource = err || data;
        const errorMessage = getApiErrorMessage(errorSource, "") || getApiErrorFallbackDetail(errorSource);
        Notify(NOTIFY.ERROR, NOTIFY.ERROR, errorMessage);
    }

    // Chọn cả folder font để tự fill tên font, size, glyph DST và preview PNG theo ký tự.
    function _renderFolderUpload() {
        return (
            <div className="folder-upload-wrapper">
                <div className="folder-upload-panel">
                    <div>
                        <div className="folder-upload-title">Upload folder font</div>
                        {folderUploadSummary ? <div className="folder-upload-summary">{folderUploadSummary}</div> : <div className="text-muted">Chọn folder có cấu trúc như Block2.</div>}
                    </div>
                    <input type="file" id="emb-font-folder-upload" className="d-none" multiple webkitdirectory="true" directory="true" onChange={_handleUploadFontFolder} />
                    <button type="button" className="btn btn-custom btn-sm" onClick={() => document.getElementById("emb-font-folder-upload").click()}>
                        <i className="fa fa-folder-open mr-2"></i>
                        Chọn folder
                    </button>
                </div>
                <div className="upload-instruction">
                    <div className="upload-instruction-title">Hướng dẫn cấu hình font thêu</div>
                    <div>Folder nên chia theo từng size, mỗi file .DST là một ký tự. Tên file có thể theo mẫu: â_1_27,78_121 #13.00.dst.</div>
                    <div>Phần trước # là ký tự và underlay; phần sau # là baseline theo mm, ví dụ #13.00 nghĩa là đường chân chữ cách mép trên file DST 13mm.</div>
                    <div>Baseline là khoảng cách từ mép trên glyph tới đường chữ. Khi upload mới, hệ thống đặt mặc định bằng chiều cao glyph.</div>
                    <div>Với chữ có chân hoặc dấu dưới như g, y, p, ạ, ộ: giảm Baseline nhỏ hơn chiều cao để phần chân/dấu được hạ xuống đúng dòng.</div>
                </div>
            </div>
        );
    }

    // Render thông tin tổng quan của bộ font.
    function _renderFontInfo() {
        return (
            <div className="font-info-grid">
                <div className="form-group">
                    <label className="required">Tên font thêu</label>
                    <input className="form-control" value={font.name || ""} onChange={(e) => _handleChangeFont("name", e.target.value)} />
                </div>
                <div className="form-group">
                    <label>Text hiển thị</label>
                    <input className="form-control" value={font.text || ""} onChange={(e) => _handleChangeFont("text", e.target.value)} placeholder="Nếu trống sẽ lấy theo tên font" />
                </div>
                <div className="form-group">
                    <label>Mã font</label>
                    <input className="form-control" value={font.code || ""} onChange={(e) => _handleChangeFont("code", e.target.value)} placeholder="vd ARIAL_BLOCK" />
                </div>
                <div className="form-group">
                    <label>Thứ tự</label>
                    <input type="number" className="form-control" value={font.sortOrder || 0} onChange={(e) => _handleChangeFont("sortOrder", e.target.value, true)} />
                </div>
                <div className="form-group d-flex align-items-center pt-4">
                    <label className="mb-0">
                        <input type="checkbox" className="mr-2" checked={font.isActive !== false} onChange={(e) => _handleChangeFont("isActive", e.target.checked)} />
                        Đang bật
                    </label>
                </div>
                <div className="form-group description-field">
                    <label>Mô tả</label>
                    <textarea className="form-control" value={font.description || ""} onChange={(e) => _handleChangeFont("description", e.target.value)} />
                </div>
                <div className="form-group">
                    <label>Preview font</label>
                    <div className="preview-upload">
                        {font.previewImageUrl ? <img src={font.previewImageUrl} alt={font.name} /> : <span className="text-muted">Chưa upload</span>}
                        <input type="file" accept="image/*" id="emb-font-preview-file" className="d-none" onChange={_handleUploadFontPreview} />
                        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => document.getElementById("emb-font-preview-file").click()}>
                            Choose file
                        </button>
                    </div>
                </div>
                <div className="form-group">
                    <label>Font hiển thị (.ttf/.otf/.woff)</label>
                    <div className="preview-upload">
                        {font.trueTypeFontFileUrl ? (
                            <a href={font.trueTypeFontFileUrl} target="_blank" rel="noreferrer" className="text-truncate">
                                Đã upload
                            </a>
                        ) : (
                            <span className="text-muted">Chưa upload</span>
                        )}
                        <input type="file" accept=".ttf,.otf,.woff,.woff2" id="emb-font-truetype-file" className="d-none" onChange={_handleUploadTrueTypeFont} />
                        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => document.getElementById("emb-font-truetype-file").click()}>
                            Choose file
                        </button>
                        {font.trueTypeFontFileUrl && (
                            <button type="button" className="btn btn-outline-danger btn-sm ml-2" onClick={() => _handleChangeFont("trueTypeFontFileUrl", "")}>
                                Xóa
                            </button>
                        )}
                    </div>
                    <small className="text-muted">Chỉ dùng cho ô nhập liệu trong editor, không ảnh hưởng file DST đem thêu.</small>
                </div>
                <div className="form-group">
                    <label>Tên font-family</label>
                    <input className="form-control" value={font.fontFamilyName || ""} onChange={(e) => _handleChangeFont("fontFamilyName", e.target.value)} placeholder="Tự lấy theo tên file nếu để trống" />
                </div>
            </div>
        );
    }

    // Render tabs size giống luồng pattern nhưng đổi nội dung sang size font chữ.
    function _renderSizeTabs() {
        return (
            <ul className="nav nav-tabs tabs-bordered">
                {font.sizes.map((size, idx) => (
                    <li key={idx} className="nav-item">
                        <a
                            className={activeSizeIndex == idx ? "nav-link active" : "nav-link btn-light"}
                            onClick={() => {
                                setFontConfigPreview(null);
                                setUnderlayTestResult(null);
                                setActiveSizeIndex(idx);
                            }}
                        >
                            <span>{size.sizeMm ? `${size.sizeMm}mm` : `Size ${idx + 1}`}</span>
                            {font.sizes.length > 1 && (
                                <i
                                    className="fa fa-times ml-2 cursor-pointer"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        _handleRemoveSize(idx);
                                    }}
                                ></i>
                            )}
                        </a>
                    </li>
                ))}
                <li className="nav-item">
                    <a className="nav-link btn-light text-custom font-weight-bold" onClick={_handleAddSize}>
                        Thêm +
                    </a>
                </li>
            </ul>
        );
    }

    // Render config của size đang chọn, gồm preview và thông số spacing mặc định.
    function _renderActiveSizeInfo() {
        if (!activeSize) return null;
        return (
            <div className="active-size-panel">
                <div className="row">
                    <div className="col-md-2 form-group">
                        <label className="required">Size</label>
                        <div className="input-group">
                            <input type="number" min="0" step="0.01" className="form-control" value={activeSize.sizeMm} onChange={(e) => _handleChangeSize(activeSizeIndex, "sizeMm", e.target.value, true)} />
                            <div className="input-group-append">
                                <span className="input-group-text">mm</span>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-2 form-group">
                        <label>Letter spacing</label>
                        <input type="number" className="form-control" value={activeSize.defaultLetterSpacingMm || 0} onChange={(e) => _handleChangeSize(activeSizeIndex, "defaultLetterSpacingMm", e.target.value, true)} />
                    </div>
                    <div className="col-md-2 form-group">
                        <label>Line spacing</label>
                        <input type="number" className="form-control" value={activeSize.defaultLineSpacingMm || 0} onChange={(e) => _handleChangeSize(activeSizeIndex, "defaultLineSpacingMm", e.target.value, true)} />
                    </div>
                    <div className="col-md-2 form-group">
                        <label>Baseline</label>
                        <input type="number" className="form-control" value={activeSize.defaultBaselineMm || 0} onChange={(e) => _handleChangeSize(activeSizeIndex, "defaultBaselineMm", e.target.value, true)} />
                    </div>
                    <div className="col-md-2 form-group">
                        <label>Preview size</label>
                        <input type="file" accept="image/*" id={`emb-font-size-preview-${activeSizeIndex}`} className="d-none" onChange={(e) => _handleUploadSizePreview(activeSizeIndex, e)} />
                        <button type="button" className="btn btn-outline-secondary btn-sm d-block" onClick={() => document.getElementById(`emb-font-size-preview-${activeSizeIndex}`).click()}>
                            Choose file
                        </button>
                    </div>
                    <div className="col-md-2 form-group">
                        <label>Tiến độ</label>
                        <div className="font-size-summary">{glyphSummary}</div>
                    </div>
                </div>
                {activeSize.previewImageUrl && <img src={activeSize.previewImageUrl} alt="" className="size-preview-image" />}
            </div>
        );
    }

    // Render toolbar thêm/upload glyph cho size đang chọn.
    function _renderGlyphToolbar() {
        if (!activeSize) return null;
        return (
            <div className="glyph-toolbar">
                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => _handleAddGlyphGroup(activeSizeIndex, "Upper")}>
                    A-Z
                </button>
                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => _handleAddGlyphGroup(activeSizeIndex, "Lower")}>
                    a-z
                </button>
                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => _handleAddGlyphGroup(activeSizeIndex, "Number")}>
                    0-9
                </button>
                <button type="button" className="btn btn-custom btn-sm" onClick={() => _handleAddGlyph(activeSizeIndex)}>
                    Thêm ký tự +
                </button>
                <input type="file" accept=".dst" multiple id={`emb-font-many-dst-${activeSizeIndex}`} className="d-none" onChange={(e) => _handleUploadManyGlyphDst(activeSizeIndex, e)} />
                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => document.getElementById(`emb-font-many-dst-${activeSizeIndex}`).click()}>
                    Upload nhiều DST
                </button>
                <button type="button" className="btn btn-outline-secondary btn-sm ml-auto" onClick={_handleTestUnderlays}>
                    Test underlay
                </button>
                <button type="button" className="btn btn-outline-custom btn-sm" onClick={_handlePreviewFontConfig}>
                    Test cấu hình
                </button>
            </div>
        );
    }

    // Render bảng kết quả test underlay để admin sửa nhanh các glyph đang thiếu hoặc sai range.
    function _renderUnderlayTestResult() {
        if (!underlayTestResult) return null;
        return (
            <div className="underlay-test-panel">
                <div className="underlay-test-header">
                    <div className="underlay-test-title">Kết quả test underlay</div>
                    <div className="text-muted">
                        Tổng {underlayTestResult.total} • OK {underlayTestResult.ok} • Cảnh báo {underlayTestResult.warning} • Lỗi {underlayTestResult.error}
                    </div>
                </div>
                <div className="underlay-test-table-wrap">
                    <table className="table table-bordered underlay-test-table">
                        <thead>
                            <tr>
                                <th style={{ width: 90 }}>Ký tự</th>
                                <th>File</th>
                                <th style={{ width: 120 }}>Stitch</th>
                                <th style={{ width: 180 }}>Underlay</th>
                                <th>Kết quả</th>
                            </tr>
                        </thead>
                        <tbody>
                            {underlayTestResult.rows.map((row, index) => (
                                <tr key={`${row.character}-${index}`} className={`underlay-row-${row.status}`}>
                                    <td className="text-center font-weight-bold">{row.character}</td>
                                    <td>{row.fileName || "-"}</td>
                                    <td className="text-center">{row.stitchCount || "-"}</td>
                                    <td>{row.underlayText}</td>
                                    <td>{row.messages.length > 0 ? row.messages.join("; ") : "OK"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    function _getFontConfigPreviewRenderData(pxPerMm, minWidth, minHeight) {
        if (!fontConfigPreview) return null;
        const preview = fontConfigPreview.svgPreview || fontConfigPreview.SvgPreview || {};
        const glyphs = preview.glyphs || preview.Glyphs || [];
        const missing = preview.missingCharacters || preview.MissingCharacters || [];
        const dst = fontConfigPreview.dst || fontConfigPreview.Dst || {};
        const dstFileUrl = dst.dstFileUrl || dst.DstFileUrl || "";
        const previewWidthMm = Number(preview.widthMm || preview.WidthMm || dst.widthMm || dst.WidthMm || 0);
        const previewHeightMm = Number(preview.heightMm || preview.HeightMm || dst.heightMm || dst.HeightMm || 0);
        const previewWidth = Math.max(minWidth, Math.ceil(previewWidthMm * pxPerMm) + 24);
        const previewHeight = Math.max(minHeight, Math.ceil(previewHeightMm * pxPerMm) + 24);
        const baselineMm = getPreviewBaselineMm(preview, activeSize && activeSize.glyphs);
        const shouldRenderDst = !!dstFileUrl;
        // SVG preview dùng hệ tọa độ mm của layout backend; DST-only preview tự fit trong renderer nên baseline được vẽ trong EmbroideryDstCanvas3D.
        const baselineStyle =
            baselineMm > 0 && glyphs.length > 0 && !shouldRenderDst
                ? {
                      left: 12,
                      top: 12 + baselineMm * pxPerMm,
                      width: Math.max(40, previewWidthMm * pxPerMm),
                  }
                : null;

        return {
            previewWidth,
            previewHeight,
            previewWidthMm,
            previewHeightMm,
            pxPerMm,
            glyphs,
            missing,
            dstFileUrl,
            shouldRenderDst,
            baselineMm,
            baselineStyle,
            previewTitle: fontConfigPreview.previewTitle || "Preview test cấu hình",
            downloadText: fontConfigPreview.downloadText || "Tải DST test",
            text: fontConfigPreview.text || fontConfigPreview.Text || "-",
        };
    }

    function _renderFontConfigPreviewCanvas(previewData, isModalPreview) {
        if (!previewData) return null;
        const { glyphs, dstFileUrl, shouldRenderDst, pxPerMm, previewWidth, previewHeight, baselineMm, baselineStyle } = previewData;
        const canvasClassName = isModalPreview ? "font-config-preview-canvas font-config-preview-canvas-modal" : "font-config-preview-canvas";

        return (
            <div className={canvasClassName} style={{ width: previewWidth, height: previewHeight }}>
                {!shouldRenderDst &&
                    glyphs.length > 0 &&
                    glyphs.map((glyph, index) => (
                        <img
                            key={`${glyph.unicodeCodePoint || glyph.UnicodeCodePoint}-${index}`}
                            src={glyph.svgFileUrl || glyph.SvgFileUrl}
                            alt={glyph.character || glyph.Character}
                            className="font-config-preview-glyph"
                            style={{
                                left: 12 + (Number(glyph.xMm || glyph.XMm) || 0) * pxPerMm,
                                top: 12 + (Number(glyph.yMm || glyph.YMm) || 0) * pxPerMm,
                                width: Math.max(1, (Number(glyph.widthMm || glyph.WidthMm) || 0) * pxPerMm),
                                height: Math.max(1, (Number(glyph.heightMm || glyph.HeightMm) || 0) * pxPerMm),
                            }}
                        />
                    ))}
                {baselineStyle && <span className="font-config-preview-baseline" style={baselineStyle}></span>}
                {shouldRenderDst && (
                    <div className="font-config-preview-dst">
                        <EmbroideryDstCanvas3D dstUrl={dstFileUrl} threadColor="#2b5cff" baselineMm={baselineMm} showBaseline useWebGL={false} />
                    </div>
                )}
                {!shouldRenderDst && glyphs.length === 0 && <div className="font-config-preview-empty">Chưa có SVG glyph hoặc file DST test để render preview.</div>}
            </div>
        );
    }

    function _openFontConfigPreviewModal(previewData) {
        if (!previewData || (previewData.glyphs.length === 0 && !previewData.dstFileUrl)) return;
        setIsShowPreviewModal(true);
    }

    function _handleFontConfigPreviewKeyDown(e, previewData) {
        if (e.key !== "Enter" && e.key !== " ") return;
        e.preventDefault();
        _openFontConfigPreviewModal(previewData);
    }

    function _handleCloseFontModal() {
        if (isShowPreviewModal) {
            setIsShowPreviewModal(false);
            return;
        }
        if (onClose) onClose();
    }

    // Render box preview từ kết quả API test; ưu tiên SVG nhanh, thiếu SVG thì dùng DST preview 3D đã compose.
    function _renderFontConfigPreview() {
        const previewData = _getFontConfigPreviewRenderData(FONT_CONFIG_PREVIEW_PX_PER_MM, FONT_CONFIG_PREVIEW_MIN_WIDTH, FONT_CONFIG_PREVIEW_MIN_HEIGHT);
        if (!previewData) return null;
        const zoomPreviewData = _getFontConfigPreviewRenderData(FONT_CONFIG_PREVIEW_MODAL_PX_PER_MM, FONT_CONFIG_PREVIEW_MODAL_MIN_WIDTH, FONT_CONFIG_PREVIEW_MODAL_MIN_HEIGHT);
        const canOpenPreviewModal = previewData.glyphs.length > 0 || previewData.dstFileUrl;
        const { previewTitle, downloadText, text, previewWidthMm, previewHeightMm, dstFileUrl, missing } = previewData;

        return (
            <div className="font-config-preview-panel">
                <div className="font-config-preview-header">
                    <div>
                        <div className="font-config-preview-title">{previewTitle}</div>
                        <div className="text-muted">
                            {text} • {previewWidthMm || 0} x {previewHeightMm || 0}mm
                        </div>
                    </div>
                    {dstFileUrl && (
                        <a href={dstFileUrl} target="_blank" rel="noreferrer" className="btn btn-outline-secondary btn-sm">
                            {downloadText}
                        </a>
                    )}
                </div>
                <div
                    className={canOpenPreviewModal ? "font-config-preview-scroll is-zoomable" : "font-config-preview-scroll"}
                    role={canOpenPreviewModal ? "button" : undefined}
                    tabIndex={canOpenPreviewModal ? 0 : undefined}
                    onClick={() => _openFontConfigPreviewModal(previewData)}
                    onKeyDown={(e) => _handleFontConfigPreviewKeyDown(e, previewData)}
                >
                    {_renderFontConfigPreviewCanvas(previewData)}
                </div>
                {missing.length > 0 && <div className="font-config-preview-warning">Chưa có SVG preview cho: {missing.join(", ")}</div>}
                {isShowPreviewModal && zoomPreviewData && (
                    <CommonModal show={isShowPreviewModal} id="embroidery-font-preview-modal" size="xl" hiddenFooter title={zoomPreviewData.previewTitle} onClose={() => setIsShowPreviewModal(false)}>
                        <ModalWrapper>
                            <div className="font-config-preview-modal-body">
                                <div className="font-config-preview-header font-config-preview-header-modal">
                                    <div>
                                        <div className="font-config-preview-title">{zoomPreviewData.previewTitle}</div>
                                        <div className="text-muted">
                                            {zoomPreviewData.text} • {zoomPreviewData.previewWidthMm || 0} x {zoomPreviewData.previewHeightMm || 0}mm
                                        </div>
                                    </div>
                                    {zoomPreviewData.dstFileUrl && (
                                        <a href={zoomPreviewData.dstFileUrl} target="_blank" rel="noreferrer" className="btn btn-outline-secondary btn-sm">
                                            {zoomPreviewData.downloadText}
                                        </a>
                                    )}
                                </div>
                                <div className="font-config-preview-scroll font-config-preview-scroll-modal">{_renderFontConfigPreviewCanvas(zoomPreviewData, true)}</div>
                                {zoomPreviewData.missing.length > 0 && <div className="font-config-preview-warning">Chưa có SVG preview cho: {zoomPreviewData.missing.join(", ")}</div>}
                            </div>
                        </ModalWrapper>
                    </CommonModal>
                )}
            </div>
        );
    }

    // Render bảng glyph của size đang chọn, mỗi dòng là một ký tự và file DST tương ứng.
    function _renderGlyphTable() {
        if (!activeSize) return null;
        const glyphs = activeSize.glyphs || [];
        return (
            <table className="table table-bordered glyph-table">
                <thead>
                    <tr className="text-center">
                        <th style={{ width: 90 }}>Ký tự</th>
                        <th style={{ width: 120 }}>Unicode</th>
                        <th>File .DST</th>
                        <th style={{ width: 190 }}>Underlay</th>
                        <th style={{ width: 90 }}>Rộng</th>
                        <th style={{ width: 90 }}>Cao</th>
                        <th style={{ width: 110 }}>Baseline</th>
                        <th style={{ width: 110 }}>Stitch</th>
                        <th style={{ width: 90 }}>Active</th>
                        <th style={{ width: 90 }}>Xóa</th>
                    </tr>
                </thead>
                <tbody>
                    {glyphs.length > 0 ? (
                        glyphs.map((glyph, glyphIndex) => (
                            <tr key={glyphIndex}>
                                <td>
                                    <input className="form-control text-center glyph-character" maxLength={2} value={glyph.character || ""} onChange={(e) => _handleChangeGlyph(activeSizeIndex, glyphIndex, "character", e.target.value)} />
                                </td>
                                <td className="text-center vertical-align-middle">{glyph.unicodeCodePoint || "-"}</td>
                                <td>
                                    <input type="file" accept=".dst" id={`emb-font-glyph-dst-${activeSizeIndex}-${glyphIndex}`} className="d-none" onChange={(e) => _handleUploadGlyphDst(activeSizeIndex, glyphIndex, e)} />
                                    <div className="glyph-dst-cell">
                                        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => document.getElementById(`emb-font-glyph-dst-${activeSizeIndex}-${glyphIndex}`).click()}>
                                            Choose file
                                        </button>
                                        {/* Có CDN URL thì cho admin tải lại file DST đã upload để kiểm tra nhanh. */}
                                        {glyph.dstFileUrl && (
                                            <a href={glyph.dstFileUrl} target="_blank" rel="noreferrer" download={glyph.dstFileName || true} className="btn btn-outline-secondary btn-sm glyph-dst-download" title="Tải file DST từ CDN">
                                                <i className="fa fa-download"></i>
                                            </a>
                                        )}
                                        <span className={glyph.dstFileName ? "glyph-dst-name" : "text-muted glyph-dst-name"}>{glyph.dstFileName || "Chưa upload"}</span>
                                    </div>
                                </td>
                                <td>
                                    <input
                                        className="form-control text-center"
                                        placeholder="1_48, 113_192"
                                        value={glyph.underlayText !== undefined ? glyph.underlayText : formatGlyphUnderlays(glyph.underlays)}
                                        onChange={(e) => _handleChangeGlyphUnderlays(activeSizeIndex, glyphIndex, e.target.value)}
                                    />
                                </td>
                                <td>
                                    <input type="number" className="form-control text-center" value={glyph.widthMm || 0} onChange={(e) => _handleChangeGlyph(activeSizeIndex, glyphIndex, "widthMm", e.target.value, true)} />
                                </td>
                                <td>
                                    <input type="number" className="form-control text-center" value={glyph.heightMm || 0} onChange={(e) => _handleChangeGlyph(activeSizeIndex, glyphIndex, "heightMm", e.target.value, true)} />
                                </td>
                                <td>
                                    <input
                                        type="number"
                                        className="form-control text-center"
                                        title="Khoảng cách từ mép trên glyph tới đường baseline. Chữ có chân như g/y/p nên nhỏ hơn chiều cao."
                                        value={glyph.baselineMm || 0}
                                        onChange={(e) => _handleChangeGlyph(activeSizeIndex, glyphIndex, "baselineMm", e.target.value, true)}
                                    />
                                </td>
                                <td>
                                    <input type="number" className="form-control text-center" value={glyph.stitchCount || 0} onChange={(e) => _handleChangeGlyph(activeSizeIndex, glyphIndex, "stitchCount", e.target.value, true)} />
                                </td>
                                <td className="text-center vertical-align-middle">
                                    <input type="checkbox" checked={glyph.isActive !== false} onChange={(e) => _handleChangeGlyph(activeSizeIndex, glyphIndex, "isActive", e.target.checked)} />
                                </td>
                                <td className="text-center vertical-align-middle">
                                    <button type="button" className="btn btn-danger btn-sm" onClick={() => _handleRemoveGlyph(activeSizeIndex, glyphIndex)}>
                                        Xóa
                                    </button>
                                </td>
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td colSpan={10} className="text-center text-muted">
                                Chưa có ký tự. Có thể thêm nhanh A-Z, a-z, 0-9 hoặc upload nhiều DST.
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        );
    }

    return (
        <React.Fragment>
            <EmbroideryFontModalGlobalStyle />
            <CommonModal show={isShowModal} id="embroidery-font-modal" size="lg" isBusy={isBusy} busyMsg="Đang xử lý..." title={id > 0 ? "Sửa font thêu" : "Thêm font thêu"} onSave={_handleSave} onClose={_handleCloseFontModal}>
                <ModalWrapper>
                    {_renderFolderUpload()}
                    {_renderFontInfo()}
                    <label className="font-weight-bold mt-3">Size và file ký tự DST</label>
                    {_renderSizeTabs()}
                    {_renderActiveSizeInfo()}
                    {_renderGlyphToolbar()}
                    {_renderUnderlayTestResult()}
                    {_renderFontConfigPreview()}
                    {_renderGlyphTable()}
                </ModalWrapper>
            </CommonModal>
        </React.Fragment>
    );
}

FontUploadModal.propTypes = {
    id: PropTypes.number,
    isShowModal: PropTypes.bool,
    onClose: PropTypes.func,
    saveSucceed: PropTypes.func,
};

const EmbroideryFontModalGlobalStyle = createGlobalStyle`
    #embroidery-font-preview-modal.modal-dialog,
    #embroidery-font-preview-modal .modal-dialog {
        width: calc(100vw - 100px) !important;
        max-width: calc(100vw - 100px) !important;
        margin-left: 50px !important;
        margin-right: 50px !important;
    }

    #embroidery-font-preview-modal.modal-dialog .modal-content,
    #embroidery-font-preview-modal .modal-content {
        max-height: calc(100vh - 56px);
    }

    #embroidery-font-preview-modal.modal-dialog .modal-body,
    #embroidery-font-preview-modal .modal-body {
        overflow: hidden;
        padding: 16px 20px 20px;
    }
`;

const PageWrapper = styled.div`
    .card-box {
        max-width: none;
        width: auto;
    }

    .emb-font-preview {
        width: 76px;
        height: 48px;
        object-fit: contain;
        background: #ffffff;
        border: 1px solid #eeeeee;
        border-radius: 4px;
    }

    .table th {
        background: #d9d9d9;
        color: #676767;
        font-size: 14px;
        font-weight: 600;
        vertical-align: middle;
    }

    .table td {
        color: #676767;
        font-size: 14px;
    }
`;

const ModalWrapper = styled.div`
    .folder-upload-wrapper {
        margin-bottom: 14px;
    }

    .folder-upload-panel {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        border: 1px solid #e5e5e5;
        border-radius: 4px;
        background: #fafafa;
        padding: 12px;
    }

    .folder-upload-title {
        color: #333333;
        font-weight: 700;
    }

    .folder-upload-summary {
        color: #2e7d32;
        font-size: 13px;
        margin-top: 4px;
    }

    .upload-instruction {
        border-left: 3px solid #e4003f;
        background: #fff7f9;
        color: #555555;
        font-size: 13px;
        line-height: 1.5;
        margin-top: 8px;
        padding: 10px 12px;
    }

    .upload-instruction-title {
        color: #333333;
        font-weight: 700;
        margin-bottom: 4px;
    }

    .font-info-grid {
        display: grid;
        grid-template-columns: 2fr 1.2fr 0.7fr 0.7fr;
        gap: 12px;
    }

    .description-field {
        grid-column: span 2;
    }

    textarea.form-control {
        min-height: 78px;
    }

    .preview-upload {
        min-height: 78px;
        border: 1px solid #dddddd;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 10px;
        padding: 8px;
    }

    .preview-upload img {
        max-width: 120px;
        max-height: 60px;
        object-fit: contain;
    }

    .active-size-panel {
        background: #f8f8f8;
        border: 1px solid #e4e4e4;
        border-top: 0;
        padding: 12px;
    }

    .font-size-summary {
        height: 34px;
        display: flex;
        align-items: center;
        color: #676767;
        font-weight: 600;
    }

    .size-preview-image {
        max-width: 180px;
        max-height: 70px;
        object-fit: contain;
        border: 1px solid #e6e6e6;
        background: #ffffff;
        padding: 6px;
    }

    .glyph-toolbar {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        align-items: center;
        padding: 12px 0;
    }

    .font-config-preview-panel {
        border: 1px solid #e1e1e1;
        background: #ffffff;
        margin-bottom: 12px;
        padding: 12px;
    }

    .font-config-preview-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        margin-bottom: 10px;
    }

    .font-config-preview-title {
        color: #333333;
        font-weight: 700;
    }

    .font-config-preview-scroll {
        max-width: 100%;
        overflow: auto;
        border: 1px dashed #cccccc;
        background: #f7f7f7;
        padding: 8px;
    }

    .font-config-preview-scroll.is-zoomable {
        cursor: zoom-in;
    }

    .font-config-preview-scroll.is-zoomable:focus {
        outline: 2px solid #65c8b7;
        outline-offset: 2px;
    }

    .font-config-preview-scroll-modal {
        max-height: calc(100vh - 210px);
        overflow: auto;
        cursor: default;
    }

    .font-config-preview-canvas {
        position: relative;
        background: #ffffff;
        min-width: 420px;
        min-height: 150px;
    }

    .font-config-preview-canvas-modal {
        min-width: calc(100vw - 170px);
        min-height: 420px;
    }

    .font-config-preview-modal-body {
        min-height: 460px;
    }

    .font-config-preview-glyph {
        position: absolute;
        display: block;
        object-fit: fill;
        z-index: 1;
    }

    .font-config-preview-baseline {
        position: absolute;
        height: 3px;
        background: #f20e45;
        border-radius: 999px;
        box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.65);
        pointer-events: none;
        transform: translateY(-50%);
        z-index: 2;
    }

    .font-config-preview-dst {
        position: absolute;
        inset: 0;
        background: #ffffff;
    }

    .font-config-preview-empty {
        color: #777777;
        display: flex;
        align-items: center;
        justify-content: center;
        height: 100%;
        padding: 12px;
        text-align: center;
    }

    .font-config-preview-warning {
        color: #c62828;
        font-size: 13px;
        margin-top: 8px;
    }

    .underlay-test-panel {
        border: 1px solid #e1e1e1;
        background: #ffffff;
        margin-bottom: 12px;
        padding: 12px;
    }

    .underlay-test-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        margin-bottom: 10px;
    }

    .underlay-test-title {
        color: #333333;
        font-weight: 700;
    }

    .underlay-test-table-wrap {
        max-height: 260px;
        overflow: auto;
    }

    .underlay-test-table {
        margin-bottom: 0;
    }

    .underlay-row-ok td {
        background: #f4fbf5;
    }

    .underlay-row-warning td {
        background: #fff8e1;
    }

    .underlay-row-error td {
        background: #ffebee;
        color: #a82020;
    }

    .glyph-table th {
        background: #eeeeee;
        color: #555555;
        vertical-align: middle;
    }

    .glyph-table td {
        vertical-align: middle;
    }

    .glyph-dst-cell {
        display: flex;
        align-items: center;
        gap: 8px;
        min-width: 260px;
    }

    .glyph-dst-download {
        width: 32px;
        flex: 0 0 32px;
        padding-left: 0;
        padding-right: 0;
        text-align: center;
    }

    .glyph-dst-name {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .glyph-character {
        font-size: 20px;
        font-weight: 700;
    }
`;
